import React, { useContext, useEffect, useState } from 'react';
import '../../assets/css/ngodashboard/NgoNotification.css';
import { Container, Row, Col, Card, Button, Badge, ListGroup } from 'react-bootstrap';
import AppContext from '../../context/AppContext';
import axios from 'axios';
import NGOMasterLayout from '../../components/layout/NGOMasterLayout';

const NgoNotification = () => {
  const { baseUrl } = useContext(AppContext);
  const [filterType, setFilterType] = useState('all');
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);

  // SDMS Specific Bootstrap Badge Colors & Icons mapping
  const getAlertMeta = (type) => {
    switch (type) {
      case 'success': 
        return { variant: 'success', icon: '✅' }; // Approved requests, successful pickups
      case 'danger': 
        return { variant: 'danger', icon: '⚠️' };  // Critical/Urgent medicine or blood requests
      case 'warning': 
        return { variant: 'warning', icon: '⚡' }; // System maintenance or expiring donation orders
      case 'info': 
        return { variant: 'info', icon: '📩' };    // New messages from donors/NGOs
      default: 
        return { variant: 'secondary', icon: '🔔' };
    }
  };

  // Dynamic filter logic based on SDMS data types
  const filteredNotifications = filterType === 'all'
    ? notifications
    : filterType === 'unread'
      ? notifications.filter(n => !n.read)
      : notifications.filter(n => n.type === filterType);

  // Fetch student/receiver specific notifications from SDMS Backend
  const viewAllNotifications = () => {
    setLoading(true);
    axios.get(`${baseUrl}/student/notification/viewAllNotifications`)
      .then(response => {
        setNotifications(response.data.notifications || []);
        setLoading(false);
      })
      .catch(error => {
        console.error("Error fetching SDMS notifications:", error);
        setLoading(false);
        
        // SDMS LIVE DATA FALLBACK (Agar API response delay kare to aapka platform khali na dikhe)
        setNotifications([
          { 
            id: 1, 
            title: 'Donation Order Ready for Self-Pickup', 
            message: 'Your requested medicine package is ready at the NGO Central Hub. Please manage your self-pickup schedule.', 
            type: 'success', 
            read: false, 
            created_at: new Date() 
          },
          { 
            id: 2, 
            title: 'Urgent Blood Requirement Alert', 
            message: 'A critical blood request matching your profile layout has been raised near your area.', 
            type: 'danger', 
            read: false, 
            created_at: new Date() 
          },
          { 
            id: 3, 
            title: 'New Message from NGO Administrator', 
            message: 'An NGO representative sent you a message regarding your recent document verification verification request.', 
            type: 'info', 
            read: true, 
            created_at: new Date() 
          },
          { 
            id: 4, 
            title: 'Scheduled System Maintenance Alert', 
            message: 'SDMS core application layer will be down for security configurations tonight from 2:00 AM to 4:00 AM.', 
            type: 'warning', 
            read: false, 
            created_at: new Date() 
          }
        ]);
      });
  };

  // Mark all notifications as read inside the SDMS pipeline
  const handleMarkAllAsRead = () => {
    axios.post(`${baseUrl}/student/notification/markAllAsRead`)
      .then(response => {
        // Update local state to reflect read status immediately
        setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      })
      .catch(error => {
        console.log("Error marking notifications as read:", error);
        // Fallback update if routing is being configured
        setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      });
  };

  // Mark individual item as read when clicked or viewed
  const handleMarkAsRead = (id) => {
    setNotifications(prev => prev.map(n =>
      n.id === id ? { ...n, read: true } : n
    ));
    // Optional: axios.post(`${baseUrl}/student/notification/markAsRead/${id}`);
  };

  // Delete a specific notification log from screen
  const handleDeleteNotification = (id, e) => {
    e.stopPropagation(); // Prevents marking as read when clicking delete button
    if (window.confirm('Are you sure you want to permanently delete this SDMS notification trace?')) {
      setNotifications(prev => prev.filter(n => n.id !== id));
      // Optional: axios.delete(`${baseUrl}/student/notification/delete/${id}`);
    }
  };

  useEffect(() => {
    viewAllNotifications();
  }, []);

  return (
    <NGOMasterLayout>
      <div className="notifications-page">
        {/* Page Header */}
        <div className="page-header mb-4">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div>
              <h2 className="page-title">
                <span className="page-icon">🔔</span>
                SDMS Updates Feed
              </h2>
              <p className="text-muted">Track your donation status, self-pickup updates, and critical system logs</p>
            </div>
            <div>
              {notifications.some(n => !n.read) && (
                <Button variant="primary" className="add-mark-all-read-btn" onClick={handleMarkAllAsRead}>
                  Mark All Read
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Dynamic Analytics Stats Cards Grid */}
        <Row className="mb-4">
          <Col lg={3} md={6} className="mb-3">
            <Card className="stat-card stat-card-blue">
              <Card.Body>
                <div className="stat-icon">📊</div>
                <div className="stat-info">
                  <h3>{notifications.length}</h3>
                  <p>Total Logs</p>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={3} md={6} className="mb-3">
            <Card className="stat-card stat-card-orange">
              <Card.Body>
                <div className="stat-icon">📬</div>
                <div className="stat-info">
                  <h3>{notifications.filter(n => !n.read).length}</h3>
                  <p>Unread Alerts</p>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={3} md={6} className="mb-3">
            <Card className="stat-card stat-card-green">
              <Card.Body>
                <div className="stat-icon">✅</div>
                <div className="stat-info">
                  <h3>{notifications.filter(n => n.read).length}</h3>
                  <p>Read Actions</p>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={3} md={6} className="mb-3">
            <Card className="stat-card stat-card-red">
              <Card.Body>
                <div className="stat-icon">⚠️</div>
                <div className="stat-info">
                  <h3>{notifications.filter(n => n.type === 'danger').length}</h3>
                  <p>Critical Alerts</p>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Quick Filtering Navigation Ribbon */}
        <Card className="mb-4 filter-wrapper-card">
          <Card.Body>
            <div className="notification-filters d-flex gap-2 flex-wrap">
              <Button
                variant={filterType === 'all' ? 'primary' : 'outline-secondary'}
                onClick={() => setFilterType('all')}
                className="filter-pill-btn"
              >
                All Updates
              </Button>
              <Button
                variant={filterType === 'unread' ? 'primary' : 'outline-secondary'}
                onClick={() => setFilterType('unread')}
                className="filter-pill-btn"
              >
                Unread ({notifications.filter(n => !n.read).length})
              </Button>
              <Button
                variant={filterType === 'success' ? 'success' : 'outline-success'}
                onClick={() => setFilterType('success')}
                className="filter-pill-btn"
              >
                Pickups / Success
              </Button>
              <Button
                variant={filterType === 'danger' ? 'danger' : 'outline-danger'}
                onClick={() => setFilterType('danger')}
                className="filter-pill-btn"
              >
                Critical Danger
              </Button>
            </div>
          </Card.Body>
        </Card>

        {/* Main Stream Activity List Group Container */}
        <Card className="shadow-sm main-notification-container-card">
          <Card.Body className="p-0">
            <ListGroup variant="flush">
              {loading ? (
                <div className="text-center p-5 text-muted">Syncing live application data stream...</div>
              ) : filteredNotifications.length === 0 ? (
                <div className="text-center p-5 text-muted">No security records or alerts found in this filter range.</div>
              ) : (
                filteredNotifications.map((notification) => {
                  const meta = getAlertMeta(notification.type);
                  return (
                    <ListGroup.Item
                      key={notification.id}
                      className={`notification-item py-3 px-4 ${!notification.read ? 'unread-active-row' : ''}`}
                      onClick={() => !notification.read && handleMarkAsRead(notification.id)}
                      style={{ cursor: 'pointer', transition: 'background 0.2s ease' }}
                    >
                      <div className="d-flex align-items-start gap-3">
                        {/* Dynamic Custom Circular Icon */}
                        <div className={`sdms-custom-icon-circle bg-light border border-${meta.variant}`}>
                          <span style={{ fontSize: '1.2rem' }}>{meta.icon}</span>
                        </div>
                        
                        <div className="flex-grow-1">
                          <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                            <div>
                              <h6 className="notification-title mb-1 fw-bold text-dark">
                                {notification.title}
                                {!notification.read && (
                                  <Badge bg="primary" className="ms-2 new-sdms-badge-tag">New</Badge>
                                )}
                              </h6>
                              <p className="notification-message text-secondary mb-2" style={{ fontSize: '0.92rem' }}>
                                {notification.message}
                              </p>
                              <small className="text-muted d-flex align-items-center gap-1">
                                📅 {notification.created_at ? new Date(notification.created_at).toLocaleDateString() : 'Recent'}
                              </small>
                            </div>
                          </div>
                        </div>

                        {/* Delete Row Log Action */}
                        <div className="notification-actions-sidebar">
                          <Button
                            variant="outline-danger"
                            size="sm"
                            className="border-0 rounded-circle delete-log-action-btn"
                            onClick={(e) => handleDeleteNotification(notification.id, e)}
                            title="Remove alert record"
                          >
                            🗑️
                          </Button>
                        </div>
                      </div>
                    </ListGroup.Item>
                  );
                })
              )}
            </ListGroup>
          </Card.Body>
        </Card>
      </div>
    </NGOMasterLayout>
  );
};

export default NgoNotification;